/**
 * =============================================================================
 * MUSIC PROFILE INTERVIEW PAGE
 * =============================================================================
 * 
 * PORTFOLIO SHOWCASE: Data-Driven Preference Collection System
 * 
 * This component demonstrates a REUSABLE PATTERN for collecting structured
 * user preferences through an engaging, interview-style wizard.
 * 
 * KEY FEATURES:
 * - Multi-step wizard with progress tracking
 * - Auto-save to localStorage (immediate) + Firebase (debounced)
 * - Graceful error handling with fallback storage
 * - Dynamic form generation from configuration arrays
 * - Extensible schema for any domain
 * 
 * REUSABILITY:
 * This pattern can be adapted for:
 * - Movie/TV preference profiles (streaming services)
 * - Fitness goal interviews (health apps)
 * - Dating compatibility questionnaires
 * - Shopping preference profiles (e-commerce)
 * - Learning style assessments (EdTech)
 * 
 * The key abstraction is:
 * 1. Define your data schema (GENRES, LANGUAGES, etc.)
 * 2. Define your steps configuration
 * 3. Render dynamic forms based on field types
 * 4. Auto-persist with local + cloud fallback
 * 
 * CULTURAL INTELLIGENCE:
 * Unique feature - captures family/heritage influences that
 * standard recommendation systems ignore. This "Musical DNA"
 * approach creates deeper personalization.
 * 
 * =============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import './MusicProfile.css';

// Country list for dropdowns
const COUNTRIES = [
  'United States', 'Mexico', 'Canada', 'United Kingdom', 'Spain', 'Argentina',
  'Brazil', 'Colombia', 'Cuba', 'Dominican Republic', 'Puerto Rico', 'France',
  'Germany', 'Italy', 'Japan', 'South Korea', 'China', 'India', 'Philippines',
  'Jamaica', 'Nigeria', 'South Africa', 'Australia', 'Ireland', 'Other'
];

// Language options
const LANGUAGES = [
  'English', 'Spanish', 'Portuguese', 'French', 'German', 'Italian',
  'Japanese', 'Korean', 'Mandarin', 'Cantonese', 'Hindi', 'Tagalog',
  'Arabic', 'Russian', 'Dutch', 'Swedish', 'Other'
];

// Music era options
const MUSIC_ERAS = [
  '1950s', '1960s', '1970s', '1980s', '1990s', '2000s', '2010s', '2020s'
];

// Genre options
const GENRES = [
  'Bachata', 'Blues', 'Bollywood', 'Boleros', 'Classical', 'Country', 
  'Cumbia', 'Electronic/EDM', 'Folk', 'Gospel', 'Hip Hop', 'Indie', 
  'Jazz', 'J-Pop', 'K-Pop', 'Latin', 'Metal', 'Oldies', 'Pop', 'Punk', 
  'R&B/Soul', 'Rancheras', 'Reggae', 'Reggaeton', 'Regional Mexican', 
  'Rock', 'Salsa', 'Trios', 'World Music', 'Other'
];

// Heritage/Ethnicity options
const HERITAGE_OPTIONS = [
  'African', 'African American', 'Asian', 'Caribbean', 'Central American',
  'Eastern European', 'Hispanic/Latino', 'Indigenous/Native American',
  'Mexican', 'Middle Eastern', 'Mixed Heritage', 'Pacific Islander', 
  'South American', 'South Asian', 'Southeast Asian', 'Western European', 
  'Prefer not to say', 'Other'
];

// Spanish Artists from Jookbox library (complete list)
const SPANISH_ARTISTS = [
  'Acerina y su Danzonera', 'Agustin Lara', 'Alberto Cortez', 'Alberto Vazquez',
  'Alejandra Guzman', 'Alejandro Fernandez', 'Alejandro Sanz', 'Amalia Mendoza',
  'Amanda Miguel', 'Ana Barbara', 'Ana Gabriel', 'Aniceto Molina', 'Antonio Aguilar',
  'Antonio Bribiesca', 'Armando Manzanero', 'Banda Blanca', 'Banda Cuisillos',
  'Banda El Limon', 'Banda El Recodo', 'Banda La Costeña', 'Banda Los Recoditos',
  'Banda Machos', 'Banda Maguey', 'Benny More', 'Bienvenido Granda', 'Bobby Pulido',
  'Bronco', 'Cafe Tacuba', 'Camilo Sesto', 'Carlos Gardel', 'Carlos Vives',
  'Celia Cruz', 'Chalino Sanchez', 'Charlie Zaa', 'Chelo', 'Chico Che',
  'Company Segundo', 'Conjunto Primavera', 'Cornelio Reyna', 'Cristian Castro',
  'Cuco Sanchez', 'Daniel Santos', 'Eddie Palmieri', 'El Gran Combo', 'El Piporro',
  'Elvis Crespo', 'Emanuel', 'Enanitos Verdes', 'Enrique Guzman', 'Enrique Iglesias',
  'Estela Nuñez', 'Eydie Gorme', 'Fernando Fernandez', 'Fito Olivares',
  'Flor Silvestre', 'Frankie Ruiz', 'Gerardo Reyes', 'Gloria Estefan', 'Gloria Trevi',
  'Grupo Cañaveral', 'Grupo Miramar', 'Grupo Mojado', 'Grupo Niche', 'Gypsy Kings',
  'Hombres G', 'Jaguares', 'Javier Solis', 'Jennifer Lopez', 'Joan Sebastian',
  'Joe Arroyo', 'Johnny Ventura', 'Jorge Negrete', 'Jose Alberto', 'Jose Alfredo Jimenez',
  'Jose Feliciano', 'Jose Jose', 'Juan Gabriel', 'Juan Luis Guerra', 'Julio Iglesias',
  'Julio Jaramillo', 'Julio Preciado', 'La Sonora Dinamita', 'La Sonora Matancera',
  'La Tropa Vallenata', 'Leo Dan', 'Leonardo Favio', 'Liberacion', 'Little Joe y La Familia',
  'Lola Beltran', 'Lorenzo De Monteclaro', 'Los Alegres de Teran', 'Los Angeles Azules',
  'Los Angeles Negros', 'Los Apson', 'Los Babys', 'Los Broncos de Reynosa',
  'Los Bukis', 'Los Cadetes de Linares', 'Los Caminantes', 'Los Dandys',
  'Los Dinamicos del Norte', 'Los Dos Oros', 'Los Enanitos Verdes',
  'Los Fabulosos Cadillacs', 'Los Freddys', 'Los Fugitivos', 'Los Hermanos Carrion',
  'Los Huracanes del Norte', 'Los Ilegales', 'Los Iracundos', 'Los Lobos',
  'Los Marineros del Norte', 'Los Moonlights', 'Los Muecas', 'Los Panchos',
  'Los Rieleros del Norte', 'Los Tecolines', 'Los Teen Tops', 'Los Temerarios',
  'Los Terricolas', 'Los Tigres Del Norte', 'Los Tiranos del Norte',
  'Los Traileros del Norte', 'Los Tres Ases', 'Los Tres Diamantes', 'Los Tres Reyes',
  'Los Tucanes De Tijuana', 'Los Van Van', 'Los Yonics', 'Lucha Reyes', 'Lucho Gatica',
  'Lucia Mendez', 'Luis Miguel', 'Lupillo Rivera', 'Machito', 'Maldita Vecindad',
  'Mana', 'Manny Manuel', 'Marc Anthony', 'Marco Antonio Solis', 'Mariachi',
  'Miguel Aceves Mejia', 'Mijares', 'Monica Naranjo', 'Nelson Ned', 'Orquesta Aragon',
  'Oscar de Leon', 'Pancho Barraza', 'Paquita la del Barrio', 'Pastor Lopez',
  'Paulina Rubio', 'Pedro Fernandez', 'Pedro Infante', 'Pedro Vargas', 'Pepe Aguilar',
  'Pepe Guizar', 'Perez Prado', 'Ramon Ayala', 'Ricardo Arjona', 'Rigo Tovar',
  'Roberto Carlos', 'Rocio Durcal', 'Rodrigo', 'Ruben Blades', 'Selena',
  'Sergio Mendez', 'Shakira', 'Silvio Rodriguez', 'Soda Stereo', 'Sonora Dinamita',
  'Sonora Matancera', 'Sonora Poncena', 'Sonora Santanera', 'Thalia', 'Tito Puente',
  'Trio Los Panchos', 'Vicente Fernandez', 'Vico C', 'Wilfrido Vargas', 'Willie Colon',
  'Willie Gonzales', 'Willy Chirino', 'Yesenia Flores'
].sort();

// English Artists from Jookbox library (complete list)
const ENGLISH_ARTISTS = [
  '10,000 Maniacs', '311', 'ABBA', 'AC/DC', 'Accept', 'Ace of Base', 'Adam Ant',
  'Aerosmith', 'Aha', 'Al Green', 'Alabama', 'Alanis Morissette', 'Albert King',
  'Alice Cooper', 'Alice in Chains', 'Alphaville', 'America', 'Aretha Franklin',
  'Art Blakey', 'Autograph', 'B-52s', 'Backstreet Boys', 'Bad Religion', 'Bananarama',
  'Bare Naked Ladies', 'Barry Manilow', 'Barry White', 'Bayside Boys', 'BB King',
  'Beach Boys', 'Beastie Boys', 'Beatles', 'Beck', 'Bee Gees', 'Ben E King', 'Berlin',
  'Big Bad Voodoo Daddy', 'Bill Withers', 'Billy Holiday', 'Billy Idol', 'Billy Joel',
  'Billy Ocean', 'Billy Ray Cyrus', 'Bjork', 'Black Sabbath', 'Blondie',
  'Blue Oyster Cult', 'Blues Brothers', 'Bob Dylan', 'Bob Marley', 'Bob Seger',
  'Bobby Brown', 'Bobby Darin', 'Bobby McFerrin', 'Bobby Vinton', 'Bon Jovi',
  'Bone Thugs N Harmony', 'Boney M', 'Booker T and The MGs', 'Boston', 'Boyz II Men',
  'Brenton Wood', 'Brian McKnight', 'Britney Spears', 'Brooks And Dunn',
  'Brothers Johnson', 'Bruce Springsteen', 'Bryan Adams', 'Buddy Holly', 'Bush',
  'Busta Rhymes', 'C+C Music Factory', 'Cake', 'Candlebox', 'Carly Simon',
  'Cat Stevens', 'Celine Dion', 'Charlie Daniels', 'Cheap Trick', 'Cher',
  'Cherry Poppin Daddies', 'Chic', 'Chris Isaak', 'Chris Ledoux', 'Christina Aguilera',
  'Chubby Checker', 'Chuck Berry', 'Clint Black', 'Collective Soul', 'Color Me Badd',
  'Commodores', 'Connie Francis', 'Coolio', 'Counting Crows', 'Cranberries', 'Creed',
  'Creedence Clearwater Revival', 'Crowded House', 'Crystal Waters', 'Cure',
  'Curtis Mayfield', 'Cypress Hill', 'Dave Edmunds', 'Dave Matthews Band',
  'David Bowie', 'Dead or Alive', 'Dee-lite', 'Def Leppard', 'Delfonics',
  'Depeche Mode', 'Destinys Child', 'Devo', 'Diana Ross', 'Dido', 'Don McLean',
  'Donny Hathaway', 'Doobie Brothers', 'Doors', 'Dr Dre', 'Dru Hill', 'Duke Ellington',
  'Duran Duran', 'Dwight Yoakam', 'Eagles', 'Earth Wind & Fire', 'Easy E', 'Eiffel 65',
  'Ella Fitzgerald', 'ELO', 'Elton John', 'Elvis Presley', 'EMF', 'Eminem', 'En Vogue',
  'Englebert Humperdink', 'English Beat', 'Enya', 'Eric Clapton', 'Escape Club',
  'Eurythmics', 'Eve 6', 'Everclear', 'Extreme', 'Faith Hill', 'Fats Domino', 'Fixx',
  'Fleetwood Mac', 'Fontella Bass', 'Four Seasons', 'Frank Sinatra', 'Fugees',
  'Gap Band', 'Garth Brooks', 'Gene Pitney', 'George Benson', 'George Harrison',
  'George Michael', 'George Strait', 'George Thorogood', 'Gino Vanelli', 'Ginuwine',
  'Glenn Campbell', 'Glenn Miller', 'Gloria Estefan', 'Godsmack', 'Goo Goo Dolls',
  'Grateful Dead', 'Green Day', 'Guns N Roses', 'Gwar', 'Hall and Oates',
  'Hank Williams JR', 'Harry Chapin', 'Harry Connick', 'Heart', 'Henry Mancini',
  'Herbie Hancock', 'Hermans Hermits', 'Herb Alpert', 'Hiroshima', 'Hot Chocolate',
  'Howard Jones', 'Ice Cube', 'Information Society', 'INXS', 'Iron Butterfly',
  'Iron Maiden', 'Jackie Wilson', 'Jackson Browne', 'Jackson Five', 'James Brown',
  'James Taylor', 'Janet Jackson', 'Jay and The Americans', 'Jefferson Airplane',
  'Jeffrey Osborne', 'Jerry Lee Lewis', 'Jewel', 'Jill Scott', 'Jim Croce',
  'Jimi Hendrix', 'Jimmy Buffett', 'Joan Osborne', 'John Coltrane', 'John Mellencamp',
  'Johnny Cash', 'Jon Secada', 'Judas Priest', 'Kansas', 'Keith Sweat', 'Kenny G',
  'Kid Rock', 'Kool and The Gang', 'Korn', 'Led Zeppelin', 'Lenny Kravitz',
  'Linda Ronstadt', 'Lita Ford', 'Live', 'LL Cool J', 'Lobo', 'Lonestar', 'Lou Rawls',
  'Lou Reed', 'Louis Armstrong', 'Luciano Pavarotti', 'Luther Vandross',
  'Lynyrd Skynyrd', 'Madness', 'Madonna', 'Main Ingredient', 'Mamas and Papas',
  'Manhattan Transfer', 'Manhattans', 'Mariah Carey', 'Marky Mark', 'Martha and The Vandellas',
  'Marvin Gaye', 'Mary Wells', 'Marilyn Manson', 'Matchbox 20', 'Mazzy Star',
  'MC Hammer', 'Megadeth', 'Men at Work', 'Men Without Hats', 'Metallica', 'Meters',
  'Michael Bolton', 'Michael Jackson', 'Midnight Oil', 'Miles Davis', 'Missing Persons',
  'Missy Elliott', 'Moby', 'Modern English', 'Molly Hatchet', 'Montell Jordan',
  'Moody Blues', 'Motley Crue', 'Nat King Cole', 'Neil Diamond', 'Neil Sedaka',
  'New Kids on the Block', 'New Order', 'Night Ranger', 'Nine Inch Nails', 'No Doubt',
  'Notorious BIG', 'NSync', 'NWA', 'O Jays', 'Offspring', 'Oingo Boingo',
  'Otis Redding', 'Outcast', 'Ozzy Osbourne', 'Pat Metheny', 'Pato Banton',
  'Paul Anka', 'Paul McCartney', 'Pavement', 'Pearl Jam', 'Peter Frampton',
  'Peter Gabriel', 'Phil Collins', 'Phish', 'Pink Floyd', 'Platters', 'Police',
  'Powerman 5000', 'Presidents of the United States', 'Prince', 'Puff Daddy',
  'Pure Prairie League', 'Queensryche', 'Quiet Riot', 'R Kelly', 'Rage Against The Machine',
  'Rammstein', 'Ramones', 'Ratt', 'Ray Charles', 'Real Life', 'Red Hot Chili Peppers',
  'REM', 'Rick James', 'Ricky Nelson', 'Rob Zombie', 'Robbie Robertson', 'Rod Stewart',
  'Roger Miller', 'Roger Whitaker', 'Rolling Stones', 'Rush', 'Sam Cooke', 'Santana',
  'Sarah Vaughan', 'Sergio Mendez', 'Shania Twain', 'Sisqo', 'Snoop Dogg', 'Sonique',
  'Spice Girls', 'Steely Dan', 'Steve Miller Band', 'Steve Winwood', 'Stevie Ray Vaughan',
  'Stevie Wonder', 'Sting', 'Stone Temple Pilots', 'Stray Cats', 'Sugar Ray',
  'Teddy Pendergrass', 'Temptations', 'Tesla', 'The Corrs', 'The Cult', 'The Doors',
  'The Offspring', 'The Who', 'Third Eye Blind', 'Three Dog Night', 'Three Doors Down',
  'Tim McGraw', 'TLC', 'Toad the Wet Sprocket', 'Tom Jones', 'Toni Braxton',
  'Tony Bennett', 'Tool', 'Tower of Power', 'Tyrone Davis', 'U2', 'UB40', 'Usher',
  'Van Halen', 'Van Morrison', 'Vanilla Ice', 'Vengaboys', 'Ventures', 'War',
  'Warren G', 'White Snake', 'Whitney Houston', 'Will Smith', 'Wyclef Jean', 'Xscape',
  'Young Rascals', 'ZZ Top'
].sort();

function MusicProfile({ user }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [existingProfile, setExistingProfile] = useState(false);
  
  // Custom genre input state
  const [motherCustomGenreInput, setMotherCustomGenreInput] = useState('');
  const [fatherCustomGenreInput, setFatherCustomGenreInput] = useState('');
  const [showMotherCustomInput, setShowMotherCustomInput] = useState(false);
  const [showFatherCustomInput, setShowFatherCustomInput] = useState(false);


  // Profile data state - initialize from localStorage if available
  const [profile, setProfile] = useState(() => {
    // Try to load from localStorage first (auto-save)
    const savedDraft = localStorage.getItem('jookbox_profile_draft');
    if (savedDraft) {
      try {
        console.log('📋 Loaded profile draft from localStorage');
        return JSON.parse(savedDraft);
      } catch (e) {
        console.error('Error parsing saved draft:', e);
      }
    }
    
    return {
      // Part 1: About You
      dateOfBirth: '',
      gender: '',
      countryOfBirth: '',
      countryRaised: '',
      currentLocation: '',
      languages: [],
      heritage: [],
      education: '',
      relationshipStatus: '',

// Part 2: Family Musical Heritage
    motherMusicEras: [],
    motherGenres: [],
    motherCustomGenres: [],
    motherArtists: '',
    motherArtistsList: [],
    fatherMusicEras: [],
    fatherGenres: [],
    fatherCustomGenres: [],
    fatherArtists: '',
    fatherArtistsList: [],
      grandparentsMusic: '',
      childhoodCarMusic: '',
      childhoodHolidayMusic: '',
      earliestMusicMemory: '',

      // Part 3: Your Music Preferences
      favoriteGenres: [],
      favoriteEras: [],
      moodPreferences: [],
      discoveryStyle: '',
      listeningTime: '',
      musicImportance: '',

      // Metadata
      completedAt: null,
      lastUpdated: null
    };
  });

  // Load existing profile from Firebase (if exists)
  useEffect(() => {
    const loadProfile = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const profileRef = doc(db, 'musicProfiles', user.uid);
        const profileSnap = await getDoc(profileRef);

        if (profileSnap.exists()) {
          const firebaseProfile = profileSnap.data();
          setProfile(prev => ({ ...prev, ...firebaseProfile }));
          setExistingProfile(true);
          // Clear draft since we have a saved profile
          localStorage.removeItem('jookbox_profile_draft');
        }
      } catch (err) {
        console.error('Error loading profile:', err);
        // Continue with localStorage draft if Firebase fails
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  // Auto-save to localStorage whenever profile changes (immediate backup)
  useEffect(() => {
    // Don't save empty profiles
    const hasData = profile.dateOfBirth || profile.languages.length > 0 || 
                    profile.favoriteGenres.length > 0 || profile.motherGenres.length > 0;
    
    if (hasData) {
      localStorage.setItem('jookbox_profile_draft', JSON.stringify(profile));
    }
  }, [profile]);

  // Auto-save to Firebase (debounced - waits 2 seconds after last change)
  useEffect(() => {
    if (!user || loading) return;
    
    const hasData = profile.dateOfBirth || profile.languages.length > 0 || 
                    profile.favoriteGenres.length > 0 || profile.motherGenres.length > 0;
    
    if (!hasData) return;

    const timeoutId = setTimeout(async () => {
      try {
        const profileData = {
          ...profile,
          lastUpdated: new Date().toISOString(),
          userId: user.uid,
          userEmail: user.email
        };
        
        const profileRef = doc(db, 'musicProfiles', user.uid);
        await setDoc(profileRef, profileData);
        console.log('☁️ Auto-saved to Firebase');
      } catch (err) {
        console.error('Auto-save to Firebase failed:', err);
        // localStorage backup is still there
      }
    }, 2000); // Wait 2 seconds after last change

    return () => clearTimeout(timeoutId);
  }, [profile, user, loading]);

  // Update profile field
  const updateField = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  // Toggle array item (for multi-select)
  const toggleArrayItem = (field, item) => {
    setProfile(prev => {
      const current = prev[field] || [];
      if (current.includes(item)) {
        return { ...prev, [field]: current.filter(i => i !== item) };
      } else {
        return { ...prev, [field]: [...current, item] };
      }
    });
  };

  // Add custom genre
  const addCustomGenre = (parent) => {
    const input = parent === 'mother' ? motherCustomGenreInput : fatherCustomGenreInput;
    const customField = parent === 'mother' ? 'motherCustomGenres' : 'fatherCustomGenres';
    
    if (input.trim()) {
      const newGenre = input.trim();
      setProfile(prev => {
        const current = prev[customField] || [];
        if (!current.includes(newGenre)) {
          return { ...prev, [customField]: [...current, newGenre] };
        }
        return prev;
      });
      
      // Clear input and hide
      if (parent === 'mother') {
        setMotherCustomGenreInput('');
        setShowMotherCustomInput(false);
      } else {
        setFatherCustomGenreInput('');
        setShowFatherCustomInput(false);
      }
    }
  };

  // Remove custom genre
  const removeCustomGenre = (parent, genre) => {
    const customField = parent === 'mother' ? 'motherCustomGenres' : 'fatherCustomGenres';
    setProfile(prev => ({
      ...prev,
      [customField]: (prev[customField] || []).filter(g => g !== genre)
    }));
  };

  // Save profile to Firebase
  const saveProfile = async () => {
    if (!user) {
      alert('Please log in to save your profile.');
      return;
    }

    setSaving(true);
    
    const profileData = {
      ...profile,
      completedAt: profile.completedAt || new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      userId: user.uid,
      userEmail: user.email
    };

    // Try up to 3 times
    let attempts = 0;
    const maxAttempts = 3;

    while (attempts < maxAttempts) {
      try {
        attempts++;
        console.log(`💾 Saving profile (attempt ${attempts})...`);
        
        const profileRef = doc(db, 'musicProfiles', user.uid);
        await setDoc(profileRef, profileData);

        console.log('✅ Profile saved successfully to Firebase');
        
        // Clear the draft from localStorage since we saved successfully
        localStorage.removeItem('jookbox_profile_draft');
        
        // Show success message
        alert('✅ Profile saved successfully!');
        navigate('/services');
        return;
        
      } catch (err) {
        console.error(`Error saving profile (attempt ${attempts}):`, err);
        
        if (attempts >= maxAttempts) {
          // Final attempt failed - but data is still in localStorage!
          alert(
            `⚠️ Could not save to cloud, but your profile is saved locally!\n\n` +
            `Error: ${err.message}\n\n` +
            `Your data is safe - try again later.`
          );
        }
      }
    }
    
    setSaving(false);
  };

  // Steps configuration
  const steps = [
    { id: 'intro', title: 'Welcome', icon: '🎵' },
    { id: 'about', title: 'About You', icon: '👤' },
    { id: 'background', title: 'Your Background', icon: '🌍' },
    { id: 'family', title: 'Family Music', icon: '👨‍👩‍👧' },
    { id: 'memories', title: 'Music Memories', icon: '💭' },
    { id: 'preferences', title: 'Your Taste', icon: '🎧' },
    { id: 'complete', title: 'Complete', icon: '✨' }
  ];

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 0));

  if (loading) {
    return (
      <div className="music-profile-page">
        <div className="profile-loading">
          <div className="loading-spinner">🎵</div>
          <p>Loading your profile...</p>
        </div>
      </div>
    );
  }

  // Render current step
  const renderStep = () => {
    switch (steps[currentStep].id) {
      case 'intro':
        return (
          <div className="step-content intro-step">
            <div className="intro-icon">🎵</div>
            <h2>Build Your Music Profile</h2>
            <p className="intro-text">
              Help us understand your musical journey. We'll ask about your background, 
              family influences, and preferences to create a personalized music experience 
              that's uniquely <strong>you</strong>.
            </p>
            <div className="intro-features">
              <div className="intro-feature">
                <span className="feature-icon">🎯</span>
                <span>Better Recommendations</span>
              </div>
              <div className="intro-feature">
                <span className="feature-icon">🌍</span>
                <span>Discover Your Heritage</span>
              </div>
              <div className="intro-feature">
                <span className="feature-icon">💫</span>
                <span>Nostalgic Throwbacks</span>
              </div>
              <div className="intro-feature">
                <span className="feature-icon">👨‍👩‍👧</span>
                <span>Family Musical DNA</span>
              </div>
            </div>
            <p className="privacy-note">
              🔒 Your data is private and only used to improve your experience. 
              Skip any question you prefer not to answer.
            </p>
            {existingProfile && (
              <p className="existing-profile-note">
                ✨ You have an existing profile. Feel free to update your answers!
              </p>
            )}
          </div>
        );

      case 'about':
        return (
          <div className="step-content">
            <h2>👤 About You</h2>
            <p className="step-description">Basic info helps us find music from your era and in your languages.</p>

            <div className="form-group">
              <label>When were you born?</label>
              <input
                type="date"
                value={profile.dateOfBirth}
                onChange={(e) => updateField('dateOfBirth', e.target.value)}
                className="form-input"
              />
              <span className="field-hint">Helps us find music from your formative years</span>
            </div>

            <div className="form-group">
              <label>Gender (optional)</label>
              <div className="radio-group">
                {['Male', 'Female', 'Non-binary', 'Prefer not to say'].map(option => (
                  <label key={option} className="radio-option">
                    <input
                      type="radio"
                      name="gender"
                      checked={profile.gender === option}
                      onChange={() => updateField('gender', option)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>What languages do you speak?</label>
              <div className="checkbox-grid">
                {LANGUAGES.map(lang => (
                  <label key={lang} className={`checkbox-option ${profile.languages.includes(lang) ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={profile.languages.includes(lang)}
                      onChange={() => toggleArrayItem('languages', lang)}
                    />
                    <span>{lang}</span>
                  </label>
                ))}
              </div>
              <span className="field-hint">We'll find great music in your languages</span>
            </div>
          </div>
        );

      case 'background':
        return (
          <div className="step-content">
            <h2>🌍 Your Background</h2>
            <p className="step-description">Where you're from shapes your musical taste.</p>

            <div className="form-group">
              <label>Country of birth</label>
              <select
                value={profile.countryOfBirth}
                onChange={(e) => updateField('countryOfBirth', e.target.value)}
                className="form-select"
              >
                <option value="">Select country...</option>
                {COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Where were you primarily raised?</label>
              <select
                value={profile.countryRaised}
                onChange={(e) => updateField('countryRaised', e.target.value)}
                className="form-select"
              >
                <option value="">Select country...</option>
                {COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
              <span className="field-hint">Regional music varies greatly!</span>
            </div>

            <div className="form-group">
              <label>Cultural heritage (select all that apply)</label>
              <div className="checkbox-grid">
                {HERITAGE_OPTIONS.map(heritage => (
                  <label key={heritage} className={`checkbox-option ${profile.heritage.includes(heritage) ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={profile.heritage.includes(heritage)}
                      onChange={() => toggleArrayItem('heritage', heritage)}
                    />
                    <span>{heritage}</span>
                  </label>
                ))}
              </div>
              <span className="field-hint">Connect with music from your roots</span>
            </div>

            <div className="form-group">
              <label>Relationship status (optional)</label>
              <div className="radio-group">
                {['Single', 'In a relationship', 'Married', 'Prefer not to say'].map(option => (
                  <label key={option} className="radio-option">
                    <input
                      type="radio"
                      name="relationship"
                      checked={profile.relationshipStatus === option}
                      onChange={() => updateField('relationshipStatus', option)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 'family':
        return (
          <div className="step-content">
            <h2>👨‍👩‍👧 Family Musical Heritage</h2>
            <p className="step-description">The music your parents loved often shapes your taste.</p>

            <div className="family-section">
              <h3>👩 Mother's Music</h3>
              
              <div className="form-group">
                <label>What era(s) of music did she love?</label>
                <div className="checkbox-grid compact">
                  {MUSIC_ERAS.map(era => (
                    <label key={era} className={`checkbox-option ${profile.motherMusicEras.includes(era) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={profile.motherMusicEras.includes(era)}
                        onChange={() => toggleArrayItem('motherMusicEras', era)}
                      />
                      <span>{era}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>What genres?</label>
                <div className="checkbox-grid">
                  {GENRES.map(genre => (
                    <label key={genre} className={`checkbox-option ${profile.motherGenres.includes(genre) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={profile.motherGenres.includes(genre)}
                        onChange={() => toggleArrayItem('motherGenres', genre)}
                      />
                      <span>{genre}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Select artists from our library (English):</label>
                <div className="checkbox-grid artists-grid">
                  {ENGLISH_ARTISTS.map(artist => (
                    <label key={artist} className={`checkbox-option ${profile.motherArtistsList.includes(artist) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={profile.motherArtistsList.includes(artist)}
                        onChange={() => toggleArrayItem('motherArtistsList', artist)}
                      />
                      <span>{artist}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Select artists from our library (Spanish):</label>
                <div className="checkbox-grid artists-grid">
                  {SPANISH_ARTISTS.map(artist => (
                    <label key={artist} className={`checkbox-option ${profile.motherArtistsList.includes(artist) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={profile.motherArtistsList.includes(artist)}
                        onChange={() => toggleArrayItem('motherArtistsList', artist)}
                      />
                      <span>{artist}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Other artists not listed above:</label>
                <input
                  type="text"
                  value={profile.motherArtists}
                  onChange={(e) => updateField('motherArtists', e.target.value)}
                  className="form-input"
                  placeholder="Add any artists not in the list above..."
                />
              </div>
            </div>

            <div className="family-section">
              <h3>👨 Father's Music</h3>
              
              <div className="form-group">
                <label>What era(s) of music did he love?</label>
                <div className="checkbox-grid compact">
                  {MUSIC_ERAS.map(era => (
                    <label key={era} className={`checkbox-option ${profile.fatherMusicEras.includes(era) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={profile.fatherMusicEras.includes(era)}
                        onChange={() => toggleArrayItem('fatherMusicEras', era)}
                      />
                      <span>{era}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>What genres?</label>
                <div className="checkbox-grid">
                  {GENRES.map(genre => (
                    <label key={genre} className={`checkbox-option ${profile.fatherGenres.includes(genre) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={profile.fatherGenres.includes(genre)}
                        onChange={() => toggleArrayItem('fatherGenres', genre)}
                      />
                      <span>{genre}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Select artists from our library (English):</label>
                <div className="checkbox-grid artists-grid">
                  {ENGLISH_ARTISTS.map(artist => (
                    <label key={artist} className={`checkbox-option ${profile.fatherArtistsList.includes(artist) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={profile.fatherArtistsList.includes(artist)}
                        onChange={() => toggleArrayItem('fatherArtistsList', artist)}
                      />
                      <span>{artist}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Select artists from our library (Spanish):</label>
                <div className="checkbox-grid artists-grid">
                  {SPANISH_ARTISTS.map(artist => (
                    <label key={artist} className={`checkbox-option ${profile.fatherArtistsList.includes(artist) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={profile.fatherArtistsList.includes(artist)}
                        onChange={() => toggleArrayItem('fatherArtistsList', artist)}
                      />
                      <span>{artist}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Other artists not listed above:</label>
                <input
                  type="text"
                  value={profile.fatherArtists}
                  onChange={(e) => updateField('fatherArtists', e.target.value)}
                  className="form-input"
                  placeholder="Add any artists not in the list above..."
                />
              </div>
            </div>
          </div>
        );

      case 'memories':
        return (
          <div className="step-content">
            <h2>💭 Music Memories</h2>
            <p className="step-description">These memories reveal deep musical connections.</p>

            <div className="form-group">
              <label>What music played during family car rides?</label>
              <textarea
                value={profile.childhoodCarMusic}
                onChange={(e) => updateField('childhoodCarMusic', e.target.value)}
                className="form-textarea"
                placeholder="Tell us about the songs or radio stations you remember..."
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>What music played during holidays or family gatherings?</label>
              <textarea
                value={profile.childhoodHolidayMusic}
                onChange={(e) => updateField('childhoodHolidayMusic', e.target.value)}
                className="form-textarea"
                placeholder="Christmas music, birthday parties, BBQs, cultural celebrations..."
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Did grandparents or extended family share any traditional music?</label>
              <textarea
                value={profile.grandparentsMusic}
                onChange={(e) => updateField('grandparentsMusic', e.target.value)}
                className="form-textarea"
                placeholder="Folk songs, cultural music, old records..."
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>What's your earliest music memory?</label>
              <textarea
                value={profile.earliestMusicMemory}
                onChange={(e) => updateField('earliestMusicMemory', e.target.value)}
                className="form-textarea"
                placeholder="A song that takes you back, a concert, dancing with family..."
                rows={3}
              />
            </div>
          </div>
        );

      case 'preferences':
        return (
          <div className="step-content">
            <h2>🎧 Your Music Taste</h2>
            <p className="step-description">Tell us what you love today.</p>

            <div className="form-group">
              <label>Your favorite genres (select all that apply)</label>
              <div className="checkbox-grid">
                {GENRES.map(genre => (
                  <label key={genre} className={`checkbox-option ${profile.favoriteGenres.includes(genre) ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={profile.favoriteGenres.includes(genre)}
                      onChange={() => toggleArrayItem('favoriteGenres', genre)}
                    />
                    <span>{genre}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Favorite music eras</label>
              <div className="checkbox-grid compact">
                {MUSIC_ERAS.map(era => (
                  <label key={era} className={`checkbox-option ${profile.favoriteEras.includes(era) ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={profile.favoriteEras.includes(era)}
                      onChange={() => toggleArrayItem('favoriteEras', era)}
                    />
                    <span>{era}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>When do you listen to music most?</label>
              <div className="radio-group">
                {['Morning', 'Afternoon', 'Evening', 'Night', 'All day'].map(option => (
                  <label key={option} className="radio-option">
                    <input
                      type="radio"
                      name="listeningTime"
                      checked={profile.listeningTime === option}
                      onChange={() => updateField('listeningTime', option)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>How important is music in your life?</label>
              <div className="radio-group vertical">
                {[
                  'Essential - Can\'t live without it',
                  'Very important - Daily listener',
                  'Moderately important - Regular listener',
                  'Background - Nice to have',
                ].map(option => (
                  <label key={option} className="radio-option">
                    <input
                      type="radio"
                      name="musicImportance"
                      checked={profile.musicImportance === option}
                      onChange={() => updateField('musicImportance', option)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className="step-content complete-step">
            <div className="complete-icon">🎉</div>
            <h2>Profile Complete!</h2>
            <p className="complete-text">
              Thank you for sharing your musical journey with us. We'll use this 
              to create a truly personalized experience.
            </p>

            <div className="profile-summary">
              <h3>Your Music DNA</h3>
              <div className="summary-items">
                {profile.languages.length > 0 && (
                  <div className="summary-item">
                    <span className="summary-label">🗣️ Languages:</span>
                    <span className="summary-value">{profile.languages.join(', ')}</span>
                  </div>
                )}
                {profile.heritage.length > 0 && (
                  <div className="summary-item">
                    <span className="summary-label">🌍 Heritage:</span>
                    <span className="summary-value">{profile.heritage.join(', ')}</span>
                  </div>
                )}
                {profile.favoriteGenres.length > 0 && (
                  <div className="summary-item">
                    <span className="summary-label">🎵 Favorite Genres:</span>
                    <span className="summary-value">{profile.favoriteGenres.slice(0, 5).join(', ')}</span>
                  </div>
                )}
                {(profile.motherGenres.length > 0 || profile.fatherGenres.length > 0) && (
                  <div className="summary-item">
                    <span className="summary-label">👨‍👩‍👧 Family Influences:</span>
                    <span className="summary-value">
                      {[...new Set([...profile.motherGenres, ...profile.fatherGenres])].slice(0, 4).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <p className="ready-text">
              ✨ Ready to discover music made for <strong>you</strong>!
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="music-profile-page">
      <div className="profile-container">
        {/* Auto-save indicator */}
        <div className="auto-save-indicator">
          <span className="auto-save-icon">☁️</span>
          <span className="auto-save-text">Auto-saving to cloud</span>
        </div>

        {/* Progress Bar */}
        <div className="progress-bar">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={`progress-step ${index <= currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              onClick={() => index < currentStep && setCurrentStep(index)}
            >
              <span className="step-icon">{step.icon}</span>
              <span className="step-title">{step.title}</span>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="step-container">
          {renderStep()}
        </div>

        {/* Navigation */}
        <div className="step-navigation">
          {currentStep > 0 && (
            <button className="nav-btn prev" onClick={prevStep}>
              ← Back
            </button>
          )}
          
          {currentStep === 0 && (
            <button className="nav-btn skip" onClick={() => navigate('/services')}>
              Skip for now
            </button>
          )}

          {currentStep < steps.length - 1 ? (
            <button className="nav-btn next" onClick={nextStep}>
              Continue →
            </button>
          ) : (
            <button 
              className="nav-btn save" 
              onClick={saveProfile}
              disabled={saving}
            >
              {saving ? 'Saving...' : '🎵 Save & Discover Music'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default MusicProfile;

